// =============================================
// Claim Extractor Service
// v4.0: Sentence-level structural detector with DB-driven language config
// Returns full sentences (not regex fragments) where ANY of 4 triggers fires:
// DATE | NUMERIC_FACT | NAMED_REFERENCE | SOURCE_STEM
// =============================================

// Default supabase client is lazy-loaded via dynamic import below — keeps this
// module standalone-tsx-friendly (Vite-only `import.meta.env` side-effect in
// "@/integrations/supabase/client" doesn't run unless we actually need it).

// =============================================
// Types
// =============================================

export type ClaimTrigger = "DATE" | "NUMERIC_FACT" | "NAMED_REFERENCE" | "SOURCE_STEM";

export interface ExtractedClaim {
  id: string;
  triggers: ClaimTrigger[];
  text: string;        // full sentence verbatim
  context: string;     // full paragraph
  h2Section: string;
  position: number;    // char offset of sentence in HTML
  score?: number;      // v5: proxy-score (triggers.length + SOURCE_STEM bonus), 1-5; set when cap selects top-N
}

interface LangConfig {
  language: string;
  sourceStemRe: RegExp;
  abbreviations: Set<string>;     // lowercased, with trailing dot ('np.', 'tj.', ...)
  particlesRe: RegExp | null;     // chained particle pattern; null if no particles
  namedRefRe: RegExp;             // pre-compiled with particles
}

// Minimal supabase client surface used here (avoid pulling Vite-aliased import at module load)
type SupaClient = {
  from: (table: string) => any;
};

let _defaultSupabase: SupaClient | null = null;
async function getDefaultSupabase(): Promise<SupaClient> {
  if (_defaultSupabase) return _defaultSupabase;
  const mod = await import("@/integrations/supabase/client");
  _defaultSupabase = mod.supabase as unknown as SupaClient;
  return _defaultSupabase;
}

// =============================================
// Constants — language-NEUTRAL only
// =============================================

const NEUTRAL_UNITS_TOKENS = [
  "%", "°[CF]", "°",
  "kg", "g", "mg", "μg", "t",
  "m", "cm", "mm", "km", "nm",
  "h", "min", "sek", "sec", "s",
  "l", "ml", "hl",
  "kWh", "MWh", "Hz", "MHz", "GHz",
  "MB", "GB", "TB",
  "km\\/h", "mph", "fps",
];
const NEUTRAL_CURRENCIES_TOKENS = [
  "\\$", "€", "£", "¥",
  "zł", "PLN", "USD", "EUR", "GBP", "JPY", "CHF", "CZK", "HUF", "SEK", "RUB", "UAH",
];

const NEUTRAL_UNITS_OR_CURRENCIES = [...NEUTRAL_UNITS_TOKENS, ...NEUTRAL_CURRENCIES_TOKENS].join("|");

// Year 1900-2099 in non-letter/non-digit context
const DATE_RE = /(?<![\p{L}\d])(?:19|20)\d{2}(?![\p{L}\d])/gu;

// Numeric followed by (a) tight unit/currency token OR (b) lowercase word ≥2 chars
const NUMERIC_FACT_RE = new RegExp(
  `(?<![\\p{L}\\d])\\d+(?:[.,]\\d+)?` +
  `(?:\\s*(?:${NEUTRAL_UNITS_OR_CURRENCIES})|\\s+\\p{Ll}{2,})`,
  "gu",
);

// Global authorities — language-neutral (cross-language acronyms)
const GLOBAL_AUTHORITIES_RE = /(?<![\p{L}\d])(?:WHO|FDA|EMA|NIH|CDC|EFSA|OECD|Eurostat|ECB|IMF|UN|EU|ISO|ICANN|NATO|UNESCO|UNICEF|GUS|NBP|UOKiK)(?![\p{L}\d])/u;

// Cap claimów verifikowanych per artykuł. Default 15 (metodyka kursu SENSAI).
// Override w runtime przez VITE_MAX_CLAIMS_PER_ARTICLE.
// MCP forensics 2026-05: cost flat po 13+ claimach ($1.49 dla 15 vs $1.67 dla 30),
// 27% nodów hitowało cap=30 → tracony ogon artykułu bez kompensacji kosztowej.
const MAX_VERIFIABLE_PER_NODE: number = (() => {
  const raw = (import.meta as { env?: Record<string, string | undefined> })?.env?.VITE_MAX_CLAIMS_PER_ARTICLE;
  const parsed = raw ? Number(raw) : 15;
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 15;
})();

// One-dot leader U+2024 — used to protect abbreviations from sentence split
const DOT_PROTECT = "․";

// =============================================
// DB config loader (in-process cache)
// =============================================

const langConfigCache = new Map<string, LangConfig>();

async function getLangConfig(language: string, client: SupaClient): Promise<LangConfig> {
  const lang = language || "pl";
  if (langConfigCache.has(lang)) return langConfigCache.get(lang)!;

  const { data, error } = await (client as any)
    .from("claim_extractor_lang_config")
    .select("source_stems, abbreviations, particles")
    .eq("language", lang)
    .single();

  if (error || !data) {
    if (lang !== "pl") return getLangConfig("pl", client);
    throw new Error("claim_extractor_lang_config: 'pl' row missing — DB not seeded");
  }

  const stems: string[] = data.source_stems || [];
  const abbrs: string[] = data.abbreviations || [];
  const particles: string[] = data.particles || [];

  const sourceStemRe = stems.length > 0
    ? new RegExp(`(?<![\\p{L}])(?:${stems.map(escapeRe).join("|")})\\p{L}*`, "iu")
    : /(?!.*)/u;  // never matches

  const particlesRe = particles.length > 0
    ? new RegExp(`(?:${particles.map(escapeRe).join("|")})`, "iu")
    : null;

  // Token = all-caps acronym ≥2 chars OR Capitalized word
  const TOKEN = "(?:\\p{Lu}{2,}|\\p{Lu}\\p{Ll}+)";
  const particlesGroup = particlesRe ? `(?:(?:${particles.map(escapeRe).join("|")})\\s+)?` : "";
  const namedRefRe = new RegExp(
    `(?<![\\p{L}])${TOKEN}(?:\\s+${particlesGroup}${TOKEN})+`,
    "gu",
  );

  const cfg: LangConfig = {
    language: lang,
    sourceStemRe,
    abbreviations: new Set(abbrs.map((a) => a.toLowerCase())),
    particlesRe,
    namedRefRe,
  };
  langConfigCache.set(lang, cfg);
  return cfg;
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// =============================================
// HTML & paragraph layer
// =============================================

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function extractParagraphs(html: string): Array<{ text: string; html: string; h2: string; position: number }> {
  const paragraphs: Array<{ text: string; html: string; h2: string; position: number }> = [];
  let currentH2 = "";

  const h2Regex = /<h2[^>]*>(.*?)<\/h2>/gi;
  const h2Positions: Array<{ title: string; position: number }> = [];
  let h2Match;
  while ((h2Match = h2Regex.exec(html)) !== null) {
    h2Positions.push({ title: stripHtml(h2Match[1]), position: h2Match.index });
  }

  const contentRegex = /<(?:p|li|td)[^>]*>([\s\S]*?)<\/(?:p|li|td)>/gi;
  let match;
  while ((match = contentRegex.exec(html)) !== null) {
    const pos = match.index;
    for (const h2 of h2Positions) {
      if (h2.position <= pos) currentH2 = h2.title;
    }
    const text = stripHtml(match[1]);
    if (text.length > 20) {
      paragraphs.push({ text, html: match[0], h2: currentH2, position: pos });
    }
  }
  return paragraphs;
}

// =============================================
// Sentence splitter (abbreviation-aware)
// =============================================

function splitSentences(
  paragraph: string,
  abbreviations: Set<string>,
): Array<{ sentence: string; offsetInPara: number }> {
  // Protect abbreviations: replace trailing dot with one-dot leader U+2024
  let protectedText = paragraph;
  for (const abbr of abbreviations) {
    if (!abbr.endsWith(".")) continue;
    const stem = abbr.slice(0, -1);
    const pattern = new RegExp(`(?<![\\p{L}])${escapeRe(stem)}\\.`, "giu");
    protectedText = protectedText.replace(pattern, (m) => m.slice(0, -1) + DOT_PROTECT);
  }

  // Split on sentence boundary: end-punctuation + whitespace + uppercase letter start
  // Lookbehind requires real .!? (after protection); lookahead requires capitalized letter
  const parts = protectedText.split(/(?<=[.!?])\s+(?=\p{Lu})/u);

  const result: Array<{ sentence: string; offsetInPara: number }> = [];
  let cursor = 0;
  for (const part of parts) {
    const restored = part.replace(new RegExp(DOT_PROTECT, "g"), ".").trim();
    if (restored.length === 0) continue;
    // Find offset in original paragraph (advances past previous sentence)
    const idx = paragraph.indexOf(restored, cursor);
    const offset = idx >= 0 ? idx : cursor;
    result.push({ sentence: restored, offsetInPara: offset });
    cursor = offset + restored.length;
  }
  return result;
}

// =============================================
// Trigger detectors
// =============================================

function hasDate(s: string): boolean {
  DATE_RE.lastIndex = 0;
  return DATE_RE.test(s);
}

function hasNumericFact(s: string): boolean {
  NUMERIC_FACT_RE.lastIndex = 0;
  return NUMERIC_FACT_RE.test(s);
}

function hasNamedReference(s: string, namedRefRe: RegExp): boolean {
  namedRefRe.lastIndex = 0;
  return namedRefRe.test(s);
}

function hasSourceStem(s: string, sourceStemRe: RegExp): boolean {
  return sourceStemRe.test(s) || GLOBAL_AUTHORITIES_RE.test(s);
}

function detectTriggers(s: string, cfg: LangConfig): ClaimTrigger[] {
  const triggers: ClaimTrigger[] = [];
  if (hasDate(s)) triggers.push("DATE");
  if (hasNumericFact(s)) triggers.push("NUMERIC_FACT");
  if (hasNamedReference(s, cfg.namedRefRe)) triggers.push("NAMED_REFERENCE");
  if (hasSourceStem(s, cfg.sourceStemRe)) triggers.push("SOURCE_STEM");
  return triggers;
}

// =============================================
// Main extraction (async — DB config lookup with cache)
// =============================================

export async function extractClaims(
  html: string,
  language: string = "pl",
  client?: SupaClient,
): Promise<ExtractedClaim[]> {
  const cfg = await getLangConfig(language || "pl", client ?? (await getDefaultSupabase()));
  const paragraphs = extractParagraphs(html);
  const claims: ExtractedClaim[] = [];
  let counter = 0;

  for (const para of paragraphs) {
    const sentences = splitSentences(para.text, cfg.abbreviations);
    for (const { sentence, offsetInPara } of sentences) {
      const triggers = detectTriggers(sentence, cfg);
      if (triggers.length === 0) continue;
      counter++;
      claims.push({
        id: `claim_${String(counter).padStart(3, "0")}`,
        triggers,
        text: sentence,
        context: para.text,
        h2Section: para.h2,
        position: para.position + offsetInPara,
      });
    }
  }

  // Dedup by lowercased+normalized sentence (keeps first occurrence)
  const seen = new Map<string, ExtractedClaim>();
  for (const c of claims) {
    const key = c.text.toLowerCase().replace(/\s+/g, " ").trim();
    if (!seen.has(key)) seen.set(key, c);
  }

  // v5 PROXY-SCORING (kurs SENSAI § "wybór najmocniejszych claimów"):
  // score = unique trigger count + bonus +1 za SOURCE_STEM (authority mention =
  // najsilniejszy sygnał fact-checkowalności: WHO/FDA/Statista/itp.).
  // Zakres 1-5. Sortuj BY score DESC, position ASC → top N → re-sort BY position
  // ASC żeby zachować kolejność dokumentu dla insert_sources w webSearchVerifier.
  const scored: ExtractedClaim[] = [...seen.values()].map((c) => ({
    ...c,
    score: c.triggers.length + (c.triggers.includes("SOURCE_STEM") ? 1 : 0),
  }));
  scored.sort((a, b) => (b.score! - a.score!) || (a.position - b.position));
  const topN = scored.slice(0, MAX_VERIFIABLE_PER_NODE);
  topN.sort((a, b) => a.position - b.position);
  return topN;
}

/**
 * Pass-through. Kept for API stability — every claim returned by extractClaims
 * is by definition verifiable (passed at least one structural trigger).
 */
export function getVerifiableClaims(claims: ExtractedClaim[]): ExtractedClaim[] {
  return claims;
}

// =============================================
// Fallback glownej tezy (2026-08-24) — artykuly definicyjne/slownikowe czesto
// nie maja liczb/dat/nazw, wiec ekstraktor zwraca 0 twierdzen i weryfikacja
// web_search W OGOLE nie startuje -> artykul konczy bez zrodel (a model
// probuje je zmyslac). Gdy claims=0, weryfikujemy PIERWSZE ZDANIE WSTEPU
// (verbatim z HTML — insertSources zakotwiczy przypis inline w intro).
// =============================================

export function buildThesisFallbackClaim(html: string, title: string): ExtractedClaim | null {
  const firstP = /<p[^>]*>([\s\S]*?)<\/p>/i.exec(html);
  if (!firstP) return null;
  const paraText = firstP[1].replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
  if (paraText.length < 40) return null;
  // pierwsze zdanie wstepu (do kropki konczacej zdanie; min 40 znakow calego akapitu)
  const sentenceMatch = /^[\s\S]*?[.!?](?=\s|$)/.exec(paraText);
  const sentence = (sentenceMatch ? sentenceMatch[0] : paraText).trim();
  if (sentence.length < 25) return null;
  return {
    id: "thesis-1",
    triggers: ["NAMED_REFERENCE"],
    text: sentence,
    context: `${title}. ${paraText}`.slice(0, 500),
    h2Section: "(intro)",
    position: firstP.index,
  };
}
